/*
 * File: components/timesheet/TimesheetTable.js
 * Mô tả: Giao diện thẻ Card cho bảng chấm công (White Mode/Mobile First).
 */
import React from 'react';

export default function TimesheetTable({ data }) {
    if (!Array.isArray(data) || data.length === 0) {
        return (
            <div className="bg-white rounded-3xl border border-gray-200 p-8 text-center shadow-sm">
                <span className="text-4xl mb-3 block">📭</span>
                <p className="text-gray-500 font-medium">Không có dữ liệu chấm công cho tháng này.</p>
            </div>
        );
    }

    // Tính tổng tất cả các trường
    const totals = data.reduce((acc, row) => {
        acc.hoursWorked += row.hoursWorked || 0;
        acc.leaveHours += row.leaveHours || 0;
        acc.h1 += row.h1 || 0;
        acc.h2 += row.h2 || 0;
        acc.h3 += row.h3 || 0;
        acc.b3 += row.b3 || 0;
        acc.b4 += row.b4 || 0;
        acc.b5 += row.b5 || 0;
        return acc;
    }, { hoursWorked: 0, leaveHours: 0, h1: 0, h2: 0, h3: 0, b3: 0, b4: 0, b5: 0 });

    const getStatusClass = (status) => {
        switch (status) {
            case 'Đi làm': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'Nghỉ phép': return 'bg-purple-50 text-purple-700 border-purple-200';
            case 'Đi làm & Nghỉ phép': return 'bg-blue-50 text-blue-700 border-blue-200';
            default: return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    return (
        <div className="space-y-4">
            
            {/* Card Tổng kết (Gắn lên đầu để dễ xem trên mobile) */}
            <div className="bg-gray-900 rounded-3xl p-5 shadow-lg text-white mb-2 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-2xl -mr-10 -mt-10 pointer-events-none"></div>
                <h3 className="text-sm font-semibold text-gray-300 mb-4">Tổng Kết Bảng Công</h3>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <p className="text-xs text-gray-400">Tổng giờ làm</p>
                        <p className="text-2xl font-black text-emerald-400">{totals.hoursWorked.toFixed(1)} <span className="text-sm font-normal text-gray-400">giờ</span></p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-400">Giờ nghỉ phép</p>
                        <p className="text-2xl font-black text-blue-400">{totals.leaveHours.toFixed(1)} <span className="text-sm font-normal text-gray-400">giờ</span></p>
                    </div>
                </div>
                
                {/* Chỉ hiện phần tăng ca/phụ cấp nếu có dữ liệu để tiết kiệm diện tích */}
                {(totals.h1 > 0 || totals.h2 > 0 || totals.b3 > 0 || totals.b4 > 0 || totals.b5 > 0) && (
                    <div className="pt-3 border-t border-gray-700/50 flex gap-3 overflow-x-auto pb-1 text-xs whitespace-nowrap scrollbar-hide">
                        {totals.h1 > 0 && <span className="bg-gray-800 px-2.5 py-1 rounded-lg text-gray-300">TC 1.5: <b className="text-white">{totals.h1}</b></span>}
                        {totals.h2 > 0 && <span className="bg-gray-800 px-2.5 py-1 rounded-lg text-gray-300">TC 2.0: <b className="text-white">{totals.h2}</b></span>}
                        {totals.b3 > 0 && <span className="bg-gray-800 px-2.5 py-1 rounded-lg text-gray-300">TC Đêm: <b className="text-white">{totals.b3}</b></span>}
                        {totals.b4 > 0 && <span className="bg-gray-800 px-2.5 py-1 rounded-lg text-gray-300">PC 0.5: <b className="text-white">{totals.b4}</b></span>}
                        {totals.b5 > 0 && <span className="bg-gray-800 px-2.5 py-1 rounded-lg text-gray-300">PC 0.3: <b className="text-white">{totals.b5}</b></span>}
                    </div>
                )}
            </div>

            {/* Danh sách các ngày công */}
            <div className="space-y-3">
                {data.map((row, index) => (
                    <div key={index} className="bg-white border border-gray-200 p-4 rounded-2xl shadow-sm hover:shadow-md transition">
                        {/* Dòng 1: Ngày tháng & Trạng thái */}
                        <div className="flex justify-between items-start mb-3">
                            <div>
                                <p className="font-bold text-gray-900 text-base">
                                    {new Date(row.date).toLocaleDateString('vi-VN', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                                </p>
                                {row.leaveType && <p className="text-[11px] font-semibold text-blue-600 mt-0.5">{row.leaveType}</p>}
                            </div>
                            <span className={`px-2.5 py-1 border text-[11px] font-bold rounded-full ${getStatusClass(row.status)}`}>
                                {row.status || 'Chưa rõ'}
                            </span>
                        </div>

                        {/* Dòng 2: Giờ vào/ra & Tổng giờ */}
                        <div className="flex justify-between items-center bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                            <div className="flex items-center gap-4">
                                <div>
                                    <span className="block text-[10px] text-gray-500 font-semibold uppercase">Vào</span>
                                    <span className="font-bold text-gray-800">{row.checkIn || '--:--'}</span>
                                </div>
                                <div className="w-px h-6 bg-gray-300"></div>
                                <div>
                                    <span className="block text-[10px] text-gray-500 font-semibold uppercase">Ra</span>
                                    <span className="font-bold text-gray-800">{row.checkOut || '--:--'}</span>
                                </div>
                            </div>
                            <div className="text-right">
                                <span className="block text-[10px] text-gray-500 font-semibold uppercase">Số giờ</span>
                                <span className="font-black text-emerald-600 text-lg">
                                    {row.hoursWorked ? row.hoursWorked.toFixed(1) : '-'}
                                </span>
                            </div>
                        </div>

                        {/* Dòng 3: Chi tiết Tăng ca / Phụ cấp (Chỉ hiển thị nếu có dữ liệu) */}
                        {(row.h1 > 0 || row.h2 > 0 || row.h3 > 0 || row.b3 > 0 || row.b4 > 0 || row.b5 > 0 || row.leaveHours > 0) && (
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                {row.h1 > 0 && <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded">TC 1.5: <span className="text-gray-900">{row.h1}</span></span>}
                                {row.h2 > 0 && <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded">TC 2.0: <span className="text-gray-900">{row.h2}</span></span>}
                                {row.h3 > 0 && <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded">TC 3.0: <span className="text-gray-900">{row.h3}</span></span>}
                                {row.b3 > 0 && <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded">TC Đêm: <span className="text-gray-900">{row.b3}</span></span>}
                                {row.b4 > 0 && <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded">PC 0.5: <span className="text-gray-900">{row.b4}</span></span>}
                                {row.b5 > 0 && <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded">PC 0.3: <span className="text-gray-900">{row.b5}</span></span>}
                                {row.leaveHours > 0 && <span className="text-[10px] font-semibold bg-blue-50 text-blue-600 border border-blue-100 px-2 py-1 rounded">Giờ phép: {row.leaveHours}</span>}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}